import { format, isSameDay, isYesterday } from "date-fns";

/**
 * Formats the last message timestamp into a readable string.
 * If the timestamp is from today, it returns the time in "h:mm a" format.
 * If the timestamp is from yesterday, it returns "Yesterday".
 * Otherwise, it returns the date in "dd-MMM-yyyy" format.
 *
 * @param lastMessageTimestamp - The timestamp of the last message.
 * @returns A formatted string representing the last message time.
 */
export function getChatLastMessageTimeString(lastMessageTimestamp: string | null) {
    if (!lastMessageTimestamp) {
        return "";
    }
    const date = new Date(lastMessageTimestamp);
    if (isSameDay(date, new Date())) {
        return format(date, "h:mm a");
    }
    if (isYesterday(date)) {
        return "Yesterday";
    }
    return format(date, "dd-MMM-yyyy");
}

/**
 * Shape Jackson emits for a Java LocalDateTime when it is not serialised as a string.
 */
export interface JavaLocalDateTime {
    year: number;
    monthValue: number;
    dayOfMonth: number;
    hour: number;
    minute: number;
    second: number;
    nano?: number;
}

/**
 * Normalises a timestamp that may arrive either as an ISO string (REST responses)
 * or as a Java LocalDateTime object (dispatcher websocket events) into an ISO string.
 * The object form is interpreted in the browser's local time zone, matching the
 * timeZone parameter the websocket connection sends to the backend.
 */
export function toIsoTimestamp(value: string | JavaLocalDateTime | null | undefined): string | null {
    if (!value) {
        return null;
    }
    if (typeof value === "string") {
        return value;
    }
    const millis = Math.floor((value.nano ?? 0) / 1_000_000);
    return new Date(
        value.year, value.monthValue - 1, value.dayOfMonth,
        value.hour, value.minute, value.second, millis,
    ).toISOString();
}